import React, {useEffect} from "react";
import {useCurrentUser, useLogout} from "~/auth/hooks";
import makeStyles from '@mui/styles/makeStyles';
import {Button, Grid, Typography, Card, CardContent, FormControlLabel} from "@mui/material";
import { Skeleton } from '@mui/material';
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {TypographyWithLabel} from "~/apps/core/ui/TypographyWithLabel";
import {Exit} from "~/ui/icons";
import {Box} from "@mui/system";
import {NotificationSwitch} from "~/notifications/NotificationSwitch";
import {ConfirmActionDialog} from "~/components/ConfirmActionDialog";


export type CurrentUserProfileProps = {
    onLogout: () => void
}


const useStyles = makeStyles(theme => ({}));


export const CurrentUserProfile = (props: CurrentUserProfileProps) => {
    const {onLogout} = props;
    const classes = useStyles(props);
    const [user, loading] = useCurrentUser();
    const [logout, logoutResult] = useLogout();
    const [isConfirmLogoutDialogOpen, setConfirmLogoutDialogOpen] = React.useState(false);
    const emails = user?.email ? user.email.split(";").map(s => s.trim()) : [];

    const handleLogoutClick = () => {
        setConfirmLogoutDialogOpen(true);
    }

    const handleConfirmLogout = async () => {
        setConfirmLogoutDialogOpen(false);
        await logout();
    }

    // trigger onLogout
    useEffect(() => {
        const {called, error, loading} = logoutResult;
        if (called && !error && !loading) {
            onLogout();
        }
    }, [logoutResult, onLogout]);

    if (!loading && !user) {
        return null;
    }

    return (
        <>
            <ConfirmActionDialog
                open={isConfirmLogoutDialogOpen}
                dialogId={"confirmLogoutDialog"}
                confirmButtonText="Подтвердить"
                onClose={() => setConfirmLogoutDialogOpen(false)}
                onConfirm={() => handleConfirmLogout()}
            >
                <Typography width="100%" align="center" variant="screenText">Выйти из личного кабинета?</Typography>
            </ConfirmActionDialog>
            <Grid container direction="column" rowSpacing="8px" wrap="nowrap">
                <Grid item>
                    <Card>
                        <CardContent>
                            <TypographyWithLabel label="Текущий пользователь">
                                {loading ? (<Skeleton variant="text"/>) : <span id="currentUserProfile.userName">{user.userName}</span>}
                            </TypographyWithLabel>
                        </CardContent>
                    </Card>
                </Grid>
                {user?.userName?.includes("Тестовый") && (
                    <>
                        <Grid item>
                            <Typography variant="screenText" color="primary">
                                Push-уведомления
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Card>
                                <CardContent>
                                    <Grid container direction="column" spacing={1}>
                                        <Grid item>
                                            <NotificationSwitch/>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
                <Grid item>
                    <Typography variant="screenText" color="primary">
                        О компании
                    </Typography>
                </Grid>
                <Grid item>
                    <Card>
                        <CardContent>
                            <Grid container direction="column" spacing={1}>
                                <Grid item>
                                    <TypographyWithLabel label="Название" noWrap={false}>
                                        {loading ? (<Skeleton variant="text"/>) : user.companyName}
                                    </TypographyWithLabel>
                                </Grid>
                                <Grid item>
                                    <TypographyWithLabel label="ИНН">
                                        {loading ? (<Skeleton variant="text"/>) : user.inn}
                                    </TypographyWithLabel>
                                </Grid>
                                <Grid item>
                                    <TypographyWithLabel label="КПП">
                                        {loading ? (<Skeleton variant="text"/>) : user.kpp}
                                    </TypographyWithLabel>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item>
                    <Typography variant="screenText" color="primary">
                        Контакты
                    </Typography>
                </Grid>
                <Grid item>
                    <Card>
                        <CardContent>
                            <Grid container direction="column" spacing={1}>
                                <Grid item>
                                    <TypographyWithLabel label="Телефон">
                                        {loading ? (<Skeleton variant="text"/>) : user.phone}
                                    </TypographyWithLabel>
                                </Grid>
                                <Grid item>
                                    <TypographyWithLabel label="Электронная почта" noWrap={false}>
                                        {loading ? (<Skeleton variant="text"/>) : emails.join(", ")}
                                    </TypographyWithLabel>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Box height="80px"/>
            <FloatingActions>
                <Button fullWidth
                        color="secondary"
                        onClick={handleLogoutClick}
                        disabled={loading}
                        startIcon={<Exit/>}
                >
                    Выйти
                </Button>
            </FloatingActions>
        </>
    )
}
